/**
 * Internal dependencies
 */
import type { EmailThread } from '@Types/emailThread';
import type { OutboundMessage } from '@Types/outboundMessage';
import { getMemberOrFirstProfile } from './fish';

export type EmailThreadMessage = {
  id: string;
  direction: 'inbound' | 'outbound';
  createdAt: Date | string;
  /** Outbound only — open telemetry rolled up from the SendGrid event webhook. */
  openedAt?: Date | null;
  /** Inbound only — the received plain-text body. */
  messageBody?: string;
  /** Outbound only — the campaign record holding the rendered body. */
  outboundMessage?: OutboundMessage;
};

/**
 * Flattens a thread's outbound deliveries and inbound messages into a single
 * chronological list. Both sides are stamped with a direction so consumers do
 * not have to infer it from the shape of the record.
 */
export function mergeThreadMessages(
  thread?: EmailThread | null,
): EmailThreadMessage[] {
  if (!thread) return [];

  const messages: EmailThreadMessage[] = [
    ...(thread.emailDeliveries ?? []).map((delivery) => ({
      id: delivery.id,
      direction: 'outbound' as const,
      createdAt: delivery.createdAt,
      openedAt: delivery.openedAt ?? null,
      outboundMessage: delivery.outboundMessage,
    })),
    ...(thread.inboundMessages ?? []).map((message) => ({
      id: message.id,
      direction: 'inbound' as const,
      createdAt: message.createdAt,
      messageBody: message.messageBody,
    })),
  ];

  return messages.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
}

/**
 * The member or staff user a thread is with. A thread is addressed to exactly
 * one of the two.
 */
export function getThreadParticipantName(thread?: EmailThread | null): string {
  if (!thread) return '';

  if (thread.fish) {
    const profile = getMemberOrFirstProfile(thread.fish);
    return `${profile?.fName ?? ''} ${profile?.lName ?? ''}`.trim();
  }

  return thread.user?.name ?? '';
}

/** Time of day for messages sent today, calendar date for anything older. */
export function formatEmailTime(value?: Date | string | null): string {
  if (!value) return '';
  const date = new Date(value);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
